import { useEffect, useId, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button.tsx";
import { Label } from "@/components/ui/label.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { resolveApiPath } from "@/lib/utils.ts";
import {
  changeShoppingTracking,
  createShoppingCheckout,
  prepareShoppingCheckout,
  prepareShoppingRecommendation,
  saveShoppingSelection,
  resolveShoppingVariant,
  shipmentLabels,
  shoppingStatusLabels,
  stopShoppingOrder,
  shoppingAccessQuery,
  shoppingOrderQuery,
  shoppingProductQuery,
  shoppingResultsQuery,
  shoppingWalletLabel,
  shoppingWalletQuery,
} from "@/shared/shoppingQueries.ts";
import type {
  CatalogProduct,
  CatalogSelection,
  CatalogVariant,
  ShoppingCardSpec,
} from "@/types/shoppingCatalog.ts";
import { formatMinorAmount } from "@/types/shopping.ts";
import {
  shoppingCheckoutHint,
  shoppingDescriptionSummary,
  shoppingImages,
  shoppingRecommendationRows,
} from "@/shared/shoppingPresentation.ts";

import { ShoppingRecommendations } from "./ShoppingRecommendations.tsx";

export function ShoppingCard({ spec }: { spec: ShoppingCardSpec }) {
  const { isSharedThread } = useArtifactViewCtx();
  const access = useQuery({ ...shoppingAccessQuery(), enabled: !isSharedThread });
  if (isSharedThread) return null;
  if (spec.kind === "order")
    return (
      <ShoppingOrder
        orderId={spec.orderId}
        canTrack={access.data?.enabled === true}
        canBuy={access.data?.enabled === true && access.data.paymentsEnabled === true}
      />
    );
  if (access.data?.enabled !== true)
    return <p className="text-sm text-muted-foreground">Shopping is unavailable.</p>;
  return <ShoppingResults spec={spec} canBuy={access.data.paymentsEnabled} />;
}

function ShoppingResults({
  spec,
  canBuy,
}: {
  spec: Extract<ShoppingCardSpec, { kind: "results" }>;
  canBuy: boolean;
}) {
  const results = useQuery(shoppingResultsQuery(spec.artifactId));
  const [selection, setSelection] = useState<CatalogSelection>();
  const [orderId, setOrderId] = useState<string>();
  const recommendation = useMutation({
    mutationFn: (option: number) =>
      prepareShoppingRecommendation(spec.artifactId, spec.threadId, option),
    onSuccess: (result) => {
      if (result.kind === "order") setOrderId(result.order.id);
      else if (result.kind === "connectLink") void results.refetch();
    },
    onError: () => {
      void results.refetch();
    },
  });
  const selectionRevision = useRef(0);
  const requestId = useRef(crypto.randomUUID());
  const detailsId = useId();
  const detailsRef = useRef<HTMLElement>(null);
  const focusDetails = useRef(false);
  const wallet = useQuery({ ...shoppingWalletQuery(), enabled: canBuy });
  const selected = selection ?? results.data?.selection;
  const needsWallet =
    !results.data?.recommendations ||
    shoppingRecommendationRows(results.data.recommendations, results.data.results.products).some(
      (row) => !row.changed && row.variant && row.variant.checkoutMode !== "merchant",
    );
  const product = useQuery({
    ...shoppingProductQuery(selected?.variantId ?? "", spec.search.country, spec.search.currency),
    enabled: Boolean(selected?.productId),
  });
  const save = useMutation({
    scope: { id: `shopping-selection:${spec.artifactId}` },
    mutationFn: (value: CatalogSelection) => saveShoppingSelection(spec.artifactId, value),
  });
  const checkout = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error("Select a product");
      const created = await createShoppingCheckout(spec.artifactId, selected, requestId.current);
      if (created.kind === "order") {
        setOrderId(created.order.id);
        await prepareShoppingCheckout(created.order.id);
      }
      return created;
    },
  });
  const choose = (value: CatalogSelection, available = true) => {
    selectionRevision.current += 1;
    checkout.reset();
    setSelection(value);
    requestId.current = crypto.randomUUID();
    if (available) save.mutate(value);
  };
  const currentVariant = product.data?.variants.find(
    (variant) => variant.id === selected?.variantId,
  );
  const variantChoice = useMutation({
    mutationFn: async (option: { name: string; label: string }) => {
      if (!selected || !currentVariant) throw new Error("Select a product first");
      const selection = selected;
      const revision = selectionRevision.current;
      const variant = await resolveShoppingVariant({
        productId: selected.productId,
        merchantDomain: currentVariant.merchantDomain,
        selected: [...currentVariant.options.filter((value) => value.name !== option.name), option],
        country: spec.search.country,
        currency: spec.search.currency,
      });
      return { variant, selection, revision };
    },
    onSuccess: ({ variant, selection, revision }) => {
      if (selectionRevision.current === revision) choose({ ...selection, variantId: variant.id });
    },
  });
  const connect = async () => {
    if (selected && !results.data?.recommendations) await save.mutateAsync(selected);
    const params = new URLSearchParams({
      purpose: "shopping",
      artifactId: spec.artifactId,
      threadId: String(spec.threadId),
    });
    window.location.assign(resolveApiPath(`/api/a/payments/link/setup?${params}`));
  };
  const connection = useMutation({ mutationFn: connect });
  useEffect(() => {
    if (!selected?.productId || !focusDetails.current) return;
    focusDetails.current = false;
    detailsRef.current?.focus({ preventScroll: true });
    detailsRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [selected?.productId]);
  useEffect(() => {
    const refresh = () => {
      void wallet.refetch();
      void results.refetch();
    };
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [wallet.refetch, results.refetch]);
  if (orderId) return <ShoppingOrder orderId={orderId} canBuy={canBuy} canTrack />;
  return (
    <div className="flex flex-col gap-3">
      <p className="font-medium">
        {results.data?.recommendations ? "My recommendation" : spec.search.query}
      </p>
      {results.isPending ? <p role="status">Loading current listings…</p> : null}
      {results.isError ? (
        <Button
          data-statsig-name="shopping-reload"
          variant="ghost"
          onClick={() => void results.refetch()}
        >
          Try loading products again
        </Button>
      ) : null}
      {!results.isPending &&
      !results.isError &&
      !results.data?.recommendations &&
      !results.data?.results.products.length ? (
        <p>No products found. Try another search.</p>
      ) : null}
      {results.data?.recommendations ? (
        <ShoppingRecommendations
          recommendations={results.data.recommendations}
          products={results.data.results.products}
          canBuy={canBuy}
          pending={recommendation.isPending}
          onPrepare={(option) => recommendation.mutate(option)}
          onDetails={(selection) => {
            focusDetails.current = true;
            setSelection(selection);
          }}
        />
      ) : (
        <section className="flex gap-3 overflow-x-auto pb-2" aria-label="Products">
          {results.data?.results.products.map((item) => (
            <ProductTile
              key={item.id}
              product={item}
              selected={selected?.productId === item.id}
              detailsId={detailsId}
              variant={selected?.productId === item.id ? currentVariant : undefined}
              onChoose={() => {
                if (selected?.productId === item.id) {
                  detailsRef.current?.focus();
                  return;
                }
                const variant = item.variants.find((v) => v.available) ?? item.variants[0];
                if (variant) {
                  focusDetails.current = true;
                  choose(
                    { productId: item.id, variantId: variant.id, quantity: 1 },
                    variant.available,
                  );
                }
              }}
            />
          ))}
        </section>
      )}
      {recommendation.isError ? (
        <p role="alert" className="text-sm">
          Couldn’t prepare this option. Retry, or ask me to reassess it.
        </p>
      ) : null}
      {canBuy &&
      wallet.data?.status !== "connected" &&
      recommendation.data?.kind === "connectLink" ? (
        <p role="status" className="text-sm">
          Your choice is saved. Connect Link below, then I can prepare it for your approval.
        </p>
      ) : null}
      {canBuy && recommendation.data?.kind === "merchant" ? (
        <a href={recommendation.data.url} target="_blank" rel="noopener noreferrer">
          Continue on merchant website ↗
        </a>
      ) : null}

      {selected ? (
        <section
          ref={detailsRef}
          id={detailsId}
          tabIndex={-1}
          aria-label="Product details"
          className="flex flex-col gap-3 border-t pt-3"
        >
          {product.isPending ? (
            <p role="status" className="text-sm">
              Loading product details…
            </p>
          ) : null}
          {product.isError ? (
            <div role="alert" className="text-sm">
              <p>Couldn’t load product details. Try again before checkout.</p>
              <Button
                data-statsig-name="shopping-product-retry"
                variant="outline"
                size="sm"
                onClick={() => void product.refetch()}
              >
                Retry product details
              </Button>
            </div>
          ) : null}
          {product.data ? (
            <>
              <p className="font-medium" aria-live="polite">
                {product.data.title}
              </p>
              <ProductGallery
                key={selected.variantId}
                product={product.data}
                variant={currentVariant}
              />
              <ProductDescription key={product.data.id} description={product.data.description} />
              {currentVariant ? (
                <a
                  className="text-sm underline"
                  href={currentVariant.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`View ${product.data.title} on ${currentVariant.merchant}`}
                >
                  View product ↗
                </a>
              ) : null}
              {!results.data?.recommendations ? (
                <>
                  {product.data.options
                    .filter((option) => option.values.length > 1)
                    .map((option) => (
                      <ProductOption
                        key={option.name}
                        label={option.name}
                        value={
                          currentVariant?.options.find((value) => value.name === option.name)
                            ?.label ?? ""
                        }
                        choices={option.values.map((value) => ({ value, label: value }))}
                        disabled={variantChoice.isPending}
                        onChange={(label) => variantChoice.mutate({ name: option.name, label })}
                      />
                    ))}
                  {currentVariant ? (
                    <p className="text-sm">
                      {currentVariant.merchant} ·{" "}
                      {formatMinorAmount(currentVariant.amountMinor, currentVariant.currency)}
                    </p>
                  ) : null}
                  {variantChoice.isError ? (
                    <p role="alert" className="text-sm text-destructive">
                      That combination is unavailable. Choose another option.
                    </p>
                  ) : null}
                  {!product.data.options.some((option) => option.values.length > 1) &&
                  product.data.variants.length > 1 ? (
                    <ProductOption
                      label="Option"
                      value={selected.variantId}
                      choices={product.data.variants.map((variant) => ({
                        value: variant.id,
                        label: `${variant.title} · ${formatMinorAmount(variant.amountMinor, variant.currency)}${variant.available ? "" : " · Out of stock"}`,
                        disabled: !variant.available,
                      }))}
                      onChange={(variantId) => choose({ ...selected, variantId })}
                    />
                  ) : null}
                  <div className="flex items-center gap-3">
                    <span className="text-sm">Quantity</span>
                    <fieldset className="flex items-center gap-2" aria-label="Quantity">
                      <Button
                        data-statsig-name="shopping-quantity-less"
                        size="icon"
                        variant="outline"
                        aria-label="Decrease quantity"
                        disabled={selected.quantity <= 1}
                        onClick={() =>
                          choose(
                            { ...selected, quantity: selected.quantity - 1 },
                            currentVariant?.available,
                          )
                        }
                      >
                        −
                      </Button>
                      <span className="min-w-6 text-center text-sm tabular-nums" aria-live="polite">
                        {selected.quantity}
                      </span>
                      <Button
                        data-statsig-name="shopping-quantity-more"
                        size="icon"
                        variant="outline"
                        aria-label="Increase quantity"
                        disabled={selected.quantity >= 100}
                        onClick={() =>
                          choose(
                            { ...selected, quantity: selected.quantity + 1 },
                            currentVariant?.available,
                          )
                        }
                      >
                        +
                      </Button>
                    </fieldset>
                  </div>
                </>
              ) : null}
            </>
          ) : null}
        </section>
      ) : null}
      {canBuy &&
      !results.data?.recommendations &&
      currentVariant?.checkoutMode === "merchant" &&
      currentVariant.available ? (
        <Button asChild data-statsig-name="shopping-merchant-checkout" size="sm">
          <a href={currentVariant.url} target="_blank" rel="noopener noreferrer">
            Continue on merchant website ↗
          </a>
        </Button>
      ) : null}
      {canBuy && checkout.data?.kind === "merchant" ? (
        <a
          href={checkout.data.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm underline"
        >
          Finish checkout on the merchant’s website
        </a>
      ) : null}
      {save.isError && selected ? (
        <div role="alert" className="flex flex-col gap-2 text-sm">
          <p>Couldn’t save your selection.</p>
          <Button
            data-statsig-name="shopping-selection-retry"
            variant="outline"
            size="sm"
            disabled={save.isPending || !currentVariant?.available}
            onClick={() => save.mutate(selected)}
          >
            Retry selection
          </Button>
        </div>
      ) : null}
      {checkout.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Couldn’t prepare checkout. Try reviewing it again.
        </p>
      ) : null}
      {connection.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Couldn’t open Link setup. Try connecting again.
        </p>
      ) : null}
      {canBuy &&
      !results.data?.recommendations &&
      wallet.data?.enabled &&
      currentVariant?.checkoutMode !== "merchant" ? (
        <p className="text-xs text-muted-foreground" role="status">
          {save.isError
            ? "Retry selection to continue."
            : shoppingCheckoutHint(selected, currentVariant, product.status)}
        </p>
      ) : null}
      {canBuy && needsWallet && wallet.data?.enabled ? (
        <div className="flex items-center justify-between gap-3 border-t pt-3">
          {wallet.data.status === "connected" ? (
            <>
              <span className="text-sm text-muted-foreground">Link connected</span>
              {!results.data?.recommendations && currentVariant?.checkoutMode !== "merchant" ? (
                <Button
                  data-statsig-name="shopping-review"
                  size="sm"
                  disabled={
                    !selected ||
                    product.isError ||
                    checkout.isPending ||
                    variantChoice.isPending ||
                    product.isFetching ||
                    save.isPending ||
                    save.isError ||
                    !product.data?.variants.find((v) => v.id === selected.variantId)?.available
                  }
                  onClick={() => checkout.mutate()}
                >
                  {checkout.isPending ? "Preparing checkout…" : "Review checkout"}
                </Button>
              ) : null}
            </>
          ) : (
            <Button
              data-statsig-name="shopping-connect"
              size="sm"
              variant="ghost"
              disabled={save.isPending || connection.isPending}
              onClick={() => connection.mutate()}
            >
              {shoppingWalletLabel(wallet.data.status)}
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}

function ProductTile({
  product,
  selected,
  detailsId,
  variant: selectedVariant,
  onChoose,
}: {
  product: CatalogProduct;
  selected: boolean;
  detailsId: string;
  variant?: CatalogVariant;
  onChoose: () => void;
}) {
  const variant =
    selectedVariant ?? product.variants.find((v) => v.available) ?? product.variants[0];
  const picture = variant?.images[0] ?? product.images[0];
  if (!variant) return null;
  return (
    <div
      className={`w-44 shrink-0 overflow-hidden rounded-lg border ${selected ? "border-primary" : "border-border"}`}
    >
      <button
        type="button"
        className="w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
        aria-label={`View details for ${product.title}`}
        aria-expanded={selected}
        aria-controls={selected ? detailsId : undefined}
        onClick={onChoose}
      >
        {picture ? (
          <img
            src={picture.url}
            alt={picture.alt}
            referrerPolicy="no-referrer"
            loading="lazy"
            className="aspect-square w-full object-contain"
          />
        ) : (
          <div className="flex aspect-square items-center justify-center bg-muted text-sm">
            No image
          </div>
        )}
        <div className="flex flex-col gap-1 p-2">
          <span className="line-clamp-2 text-sm font-medium">{product.title}</span>
          <span className="text-sm">
            {formatMinorAmount(variant.amountMinor, variant.currency)}
          </span>
          <span className="text-xs text-muted-foreground">{variant.merchant}</span>
          {variant.rating ? (
            <span className="text-xs">
              ★ {variant.rating.value} ({variant.rating.count})
            </span>
          ) : null}
          <span className="text-xs">{variant.available ? "In stock" : "Out of stock"}</span>
          <span className="text-xs font-medium text-primary">View details</span>
        </div>
      </button>
      <a
        href={variant.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block p-2 text-xs underline"
        aria-label={`View ${product.title} on ${variant.merchant}`}
      >
        View product ↗
      </a>
    </div>
  );
}

function ProductOption({
  label,
  value,
  choices,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  choices: Array<{ value: string; label: string; disabled?: boolean }>;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} disabled={disabled} onValueChange={onChange}>
        <SelectTrigger id={id}>
          <SelectValue placeholder="Choose an option" />
        </SelectTrigger>
        <SelectContent>
          {choices
            .filter((choice) => choice.value)
            .map((choice) => (
              <SelectItem key={choice.value} value={choice.value} disabled={choice.disabled}>
                {choice.label}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function ProductDescription({ description }: { description: string }) {
  const [expanded, setExpanded] = useState(false);
  const id = useId();
  const summary = shoppingDescriptionSummary(description);
  if (!description) return null;
  return (
    <div className="text-sm">
      <p id={id} className="text-muted-foreground">
        {expanded ? description : summary}
      </p>
      {summary !== description ? (
        <Button
          data-statsig-name="shopping-description"
          variant="link"
          size="sm"
          className="px-0"
          aria-expanded={expanded}
          aria-controls={id}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Read less" : "Read more"}
        </Button>
      ) : null}
    </div>
  );
}

function ProductGallery({
  product,
  variant,
}: {
  product: CatalogProduct;
  variant?: CatalogVariant;
}) {
  const [failed, setFailed] = useState<string[]>([]);
  const images = shoppingImages(product, variant).filter((image) => !failed.includes(image.url));
  const [activeUrl, setActiveUrl] = useState<string>();
  const active = images.find((image) => image.url === activeUrl) ?? images[0];
  if (!active) return null;
  return (
    <section className="flex flex-col gap-2" aria-label={`Photos of ${product.title}`}>
      <img
        className="h-64 w-full rounded-lg bg-card object-contain"
        src={active.url}
        alt={active.alt || product.title}
        referrerPolicy="no-referrer"
        onError={() => setFailed((value) => [...value, active.url])}
      />
      {images.length > 1 ? (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {images.map((image, index) => (
            <button
              key={image.url}
              type="button"
              aria-label={`Show photo ${index + 1} of ${images.length}`}
              aria-pressed={image.url === active.url}
              onClick={() => setActiveUrl(image.url)}
              className={`h-14 w-14 shrink-0 overflow-hidden rounded-md border focus-visible:ring-2 focus-visible:ring-ring ${image.url === active.url ? "border-primary" : ""}`}
            >
              <img
                src={image.url}
                alt=""
                className="h-full w-full object-contain"
                loading="lazy"
                referrerPolicy="no-referrer"
                onError={() => setFailed((value) => [...value, image.url])}
              />
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function ShoppingOrder({
  orderId,
  canBuy,
  canTrack,
}: {
  orderId: string;
  canBuy: boolean;
  canTrack: boolean;
}) {
  const client = useQueryClient();
  const query = shoppingOrderQuery(orderId);
  const order = useQuery(query);
  const prepare = useMutation({
    mutationFn: () => prepareShoppingCheckout(orderId),
    onSuccess: (data) => client.setQueryData(query.queryKey, data),
  });
  const stop = useMutation({
    mutationFn: () => stopShoppingOrder(orderId),
    onSuccess: (data) => client.setQueryData(query.queryKey, data),
  });
  const tracking = useMutation({
    mutationFn: (enabled: boolean) => changeShoppingTracking(orderId, enabled),
    onSuccess: (data) => client.setQueryData(query.queryKey, data),
  });
  if (!order.data)
    return order.isError ? (
      <div role="alert" className="flex flex-col gap-2 text-sm">
        <p>Order could not be loaded.</p>
        <Button
          data-statsig-name="shopping-order-retry"
          variant="outline"
          onClick={() => void order.refetch()}
        >
          Retry order
        </Button>
      </div>
    ) : (
      <p role="status">Loading order…</p>
    );
  const value = order.data;
  return (
    <div className="flex flex-col gap-3">
      <p className="font-medium">{value.title}</p>
      <p className="text-sm text-muted-foreground">
        {value.merchant} · Quantity {value.quantity}
      </p>
      <p role="status" className="text-sm">
        {shoppingStatusLabels[value.status]}
      </p>
      {canBuy && !["confirmed", "cancelled"].includes(value.status) && value.paymentArtifactId ? (
        <Button asChild data-statsig-name="shopping-review-order">
          <a href={`/artifacts/${encodeURIComponent(value.paymentArtifactId)}`}>
            {value.status === "awaitingApproval" ? "Review order" : "Open purchase details"}
          </a>
        </Button>
      ) : null}
      {value.order ? (
        <p className="text-sm">
          Order {value.order.orderId} ·{" "}
          {formatMinorAmount(value.order.amountMinor, value.order.currency)}
        </p>
      ) : null}
      {value.shipments.map((shipment) => (
        <div key={shipment.id} className="rounded-md bg-muted p-3 text-sm">
          <p>{shipmentLabels[shipment.stage]}</p>
          {shipment.estimatedDelivery ? (
            <p>Estimated delivery: {shipment.estimatedDelivery}</p>
          ) : null}
          {shipment.trackingUrl ? (
            <a
              className="underline"
              href={shipment.trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Track with {shipment.carrier ?? "carrier"}
            </a>
          ) : null}
        </div>
      ))}
      {canBuy && value.status === "selected" ? (
        <Button
          data-statsig-name="shopping-prepare"
          disabled={prepare.isPending}
          onClick={() => prepare.mutate()}
        >
          Prepare checkout
        </Button>
      ) : null}
      {!["confirmed", "cancelled"].includes(value.status) ? (
        <Button
          data-statsig-name="shopping-stop"
          variant="ghost"
          disabled={stop.isPending}
          onClick={() => stop.mutate()}
        >
          Stop purchase automation
        </Button>
      ) : null}
      {value.status === "confirmed" ? (
        <Button
          data-statsig-name="shopping-tracking"
          variant="ghost"
          disabled={tracking.isPending || (!value.tracking && !canTrack)}
          onClick={() => tracking.mutate(!value.tracking)}
        >
          {value.tracking ? "Stop delivery updates" : "Track delivery"}
        </Button>
      ) : null}
      {prepare.isError || tracking.isError || stop.isError ? (
        <p role="alert">Couldn’t update this order. Refresh and try again.</p>
      ) : null}
      <a
        className="text-sm underline"
        href={value.productUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        View product ↗
      </a>
    </div>
  );
}
